/**
 * GeneratorCoordinationCard — P3 hotel generator pre-storm checklist.
 * Shown only to P3 (Long Bay Reef Resort) on the hurricane prep page.
 * Atlas: E5 (P3-only section).
 */
"use client";

import { useState } from "react";
import { CheckCircle2, Circle, Zap } from "lucide-react";
import { HOTEL_GENERATOR_CHECKLIST } from "@petci/fixtures";
import { useT } from "@/lib/locale";

export function GeneratorCoordinationCard() {
  const t = useT();
  const [checked, setChecked] = useState<Set<string>>(new Set());

  function toggle(id: string) {
    setChecked((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  const done = checked.size;
  const total = HOTEL_GENERATOR_CHECKLIST.length;

  return (
    <div className="card-surface border-l-4 border-l-semantic-warning space-y-3">
      <div className="flex items-center gap-2">
        <Zap size={16} className="text-semantic-warning-text" aria-hidden />
        <h2 className="text-sm font-semibold text-neutral-800">
          {t("hurricane.generatorCoordination")}
        </h2>
        <span className="text-xs text-neutral-500 ml-auto">
          {done}/{total}
        </span>
      </div>
      <p className="text-xs text-neutral-500">
        {t("hurricane.generatorIntro")}
      </p>
      <ul className="space-y-2">
        {HOTEL_GENERATOR_CHECKLIST.map((item) => {
          const isChecked = checked.has(item.id);
          return (
            <li key={item.id}>
              <button
                onClick={() => toggle(item.id)}
                className="w-full flex items-start gap-3 text-left group"
                aria-pressed={isChecked}
                aria-label={t(item.labelKey)}
              >
                <span className="mt-0.5 flex-shrink-0">
                  {isChecked ? (
                    <CheckCircle2 size={16} className="text-semantic-success-text" aria-hidden />
                  ) : (
                    <Circle size={16} className="text-neutral-500 group-hover:text-semantic-warning-text transition-colors duration-fast" aria-hidden />
                  )}
                </span>
                <div className="flex-1 min-w-0">
                  <p
                    className={[
                      "text-sm",
                      isChecked ? "line-through text-neutral-500" : "text-neutral-800",
                    ].join(" ")}
                  >
                    {t(item.labelKey)}
                  </p>
                  {item.detailKey && (
                    <p className="text-xs text-neutral-500 mt-0.5">{t(item.detailKey)}</p>
                  )}
                </div>
              </button>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
